import {
    WorkflowFormKey,
    WorkflowTaskDefinitionKey,
    ITask,
    ITaskListPreview,
    TaskHint,
    TaskPreviewAdditionalInformation,
    TaskPreviewAdditionalInformationOptions,
    TaskSubject,
} from '@lib/workflow-integration/forms';
import { ITaskInformation, PriceFormatter } from '@lib/util';
import { IPerson } from '@lib/user';
import { CustomerDepartmentDto } from '@lib/department/ui';
import { InvoiceDetail } from '../../detail/types/InvoiceDetail';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { View } from '@lib/core';
import { IStandaloneInvoiceDetailDto } from '../../detail/types/IStandaloneInvoiceDetail';
import { supplierDisplayName } from '@lib/supplier';

export interface IInvoiceTask extends ITask {
    invoice: IStandaloneInvoiceDetailDto;
    userAllowedToApprove: boolean;
}

export class InvoiceTask implements ITask {
    assignee: string;
    created: string;
    invoice: InvoiceDetail;
    userAllowedToApprove: boolean;
    formKey: WorkflowFormKey;
    id: string;
    name: string;
    taskDefinitionKey: WorkflowTaskDefinitionKey;
    hint: TaskHint;

    public readonly info: ITaskInformation;

    constructor(dto: IInvoiceTask) {
        this.assignee = dto.assignee;
        this.created = dto.created;
        this.formKey = dto.formKey;
        this.invoice = InvoiceDetail.fromStandalone(dto.invoice);
        this.userAllowedToApprove = dto.userAllowedToApprove;
        this.id = dto.id;
        this.name = dto.name;
        this.taskDefinitionKey = dto.taskDefinitionKey;
        this.info = {
            id: dto.id,
            assignee: dto.assignee,
        };
        this.hint = dto.hint;
    }
}

export interface IInvoiceTaskPreview extends ITaskListPreview {
    invoiceNumber: string;
    invoiceId: number;
    totalNetPrice: number;
    orderNumbers: string[];
    currency: string;
    supplier?: { name: string; supplierNumber?: string | null } | null;
    department?: CustomerDepartmentDto | null;
}

export class InvoiceTaskPreview implements ITaskListPreview {
    public readonly id: string;
    public readonly created: string;
    public readonly formKey: string;
    public readonly displayName: string;
    public readonly subject: TaskSubject;
    public readonly subjectIdentifier?: string;
    public readonly additionalInformation?: TaskPreviewAdditionalInformation[];
    public readonly invoiceNumber: string;
    public readonly orderNumbers: string[];
    public readonly invoiceId: number;
    public readonly totalNetPrice: number;
    public readonly assignee?: IPerson | null;
    public readonly department?: CustomerDepartmentDto | null;

    constructor(dto: IInvoiceTaskPreview, displayName: string) {
        this.assignee = dto.assignee;
        this.created = dto.created;
        this.formKey = dto.formKey;
        this.id = dto.id;
        this.orderNumbers = dto.orderNumbers;
        this.displayName = displayName;
        this.subject = TaskSubject.INVOICE;
        this.subjectIdentifier = dto.invoiceNumber;
        this.department = dto.department ?? null;

        const additionalInformation: TaskPreviewAdditionalInformation[] = [];

        if (dto.department?.name) {
            additionalInformation.push(
                new TaskPreviewAdditionalInformation(marker('Bedarfsträger'), [dto.department.name]),
            );
        }

        if (dto.supplier?.name) {
            additionalInformation.push(
                new TaskPreviewAdditionalInformation(marker('Lieferant'), [
                    supplierDisplayName(dto.supplier.supplierNumber, dto.supplier.name),
                ]),
            );
        }

        additionalInformation.push(
            new TaskPreviewAdditionalInformation(marker('Gesamtpreis (Netto)'), [
                PriceFormatter.format(dto.totalNetPrice, dto.currency),
            ]),
        );

        const options: TaskPreviewAdditionalInformationOptions = {
            copyToClipboard: true,
            route: [View.CLIENT, 'order', '[entry]', 'detail'],
        };

        if (dto.orderNumbers?.length === 1) {
            additionalInformation.push(
                new TaskPreviewAdditionalInformation(marker('Bestellung'), [dto.orderNumbers[0]], options),
            );
        } else if (dto.orderNumbers?.length > 1) {
            additionalInformation.push(
                new TaskPreviewAdditionalInformation(marker('Bestellungen'), dto.orderNumbers, options),
            );
        }

        this.additionalInformation = additionalInformation;
        this.invoiceNumber = dto.invoiceNumber;
        this.invoiceId = dto.invoiceId;
        this.totalNetPrice = dto.totalNetPrice;
    }
}
